'use client';

import type { MeResponse } from '@/lib/api';
import { formatRelative } from '@/lib/format';

// Палитра для динамического avatar-градиента — детерминирована по telegram_id.
// Цвета через CSS-переменные (см. globals.css → :root --color-theme-*),
// которые синхронизированы с tailwind.config.ts → theme.colors.theme.
// Inline-style тут необходим (динамика по hash юзера), но без хардкод-хексов.
const GRADIENTS: ReadonlyArray<readonly [string, string]> = [
  ['var(--color-theme-elections)', 'var(--color-theme-domestic)'],
  ['var(--color-theme-conflicts)', 'var(--color-theme-sanctions)'],
  ['var(--color-theme-diplomacy)', 'var(--color-theme-elections)'],
  ['var(--color-theme-sanctions)', 'var(--color-theme-conflicts)'],
  ['var(--color-theme-geopolitics)', 'var(--color-theme-conflicts)'],
  ['var(--color-theme-domestic)', 'var(--color-theme-diplomacy)'],
];

function avatarColors(telegramId: number): readonly [string, string] {
  return GRADIENTS[telegramId % GRADIENTS.length]!;
}

export function HeroCard({
  user,
  isOperator,
  operatorRole,
}: {
  user: MeResponse['user'];
  isOperator: boolean;
  operatorRole: string | null;
}) {
  const [c1, c2] = avatarColors(user.telegram_id);
  const initial = (user.first_name.trim()[0] ?? '?').toUpperCase();
  const ageDays = (Date.now() / 1000 - user.created_at) / 86400;
  // formatRelative даёт "только что" / "5 мин назад" / "2ч назад" / "вчера" /
  // "3д назад" / "12 апр". Для UI хедера всё в верхнем регистре, и для
  // "12 апр" префикс "С НАМИ" звучит косноязычно — заменяем на эмодзи 📅.
  let memberLabel: string;
  if (ageDays < 1) {
    memberLabel = 'НОВЫЙ';
  } else if (ageDays < 7) {
    memberLabel = `С НАМИ ${formatRelative(user.created_at).toUpperCase()}`;
  } else {
    memberLabel = `📅 ${formatRelative(user.created_at).toUpperCase()}`;
  }

  return (
    <div className="flex items-center gap-3.5 mb-5">
      {/* Sharp diamond avatar */}
      <div className="relative w-14 h-14 shrink-0">
        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(135deg, ${c1}, ${c2})`,
            transform: 'rotate(45deg)',
          }}
        />
        <div
          className="absolute display text-bg-base text-[24px] inset-0 flex items-center justify-center"
        >
          {initial}
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <h1 className="display text-[22px] leading-tight tracking-[-0.01em] truncate">
          {user.first_name.toUpperCase()}
        </h1>
        <p className="font-mono text-[11px] text-text-tertiary tracking-[0.05em] truncate">
          @{user.username ?? 'без_ника'} · #{user.telegram_id}
        </p>
        <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
          {isOperator && (
            <span className="px-2 py-0.5 bg-accent text-bg-base display text-[10px] tracking-[0.08em]">
              ⚙️ {operatorRole === 'founder' ? 'FOUNDER' : (operatorRole ?? 'OPERATOR').toUpperCase()}
            </span>
          )}
          {user.is_flagged && (
            <span className="px-2 py-0.5 bg-no/20 text-no border border-no/30 font-mono text-[10px] tracking-[0.1em]">
              ⚠ FLAGGED
            </span>
          )}
          <span className="font-mono text-[10px] text-text-tertiary tracking-[0.1em]">
            {memberLabel}
          </span>
        </div>
      </div>
    </div>
  );
}
